import React, { useState, useRef, useEffect } from "react";
import ActionCardView from "../cards/ActionCardView";

const MobileHand = ({
  playerActionCards = [],
  newActionCardIds = [],
  setNewActionCardIds,
  handleSelectActionCard,
  myTurn,
  actionsRemaining,
  name,
  selectedActionCard,
  styleCardSize,
  maxActionCards,
  onDrawActionCard,
  canDrawActionCard,
  handFull,
  redrawMode = false,
  selectedForDiscard = new Set(),
  hasUsedRedrawThisTurn = false,
  onEnterRedrawMode,
  onToggleDiscardSelection,
  onCancelRedraw,
  onConfirmRedraw,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const handleTouchStart = (e) => {
    setStartY(e.touches[0].clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    setCurrentY(e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    
    const deltaY = startY - currentY;
    const threshold = 50;

    if (Math.abs(deltaY) > threshold) {
      if (deltaY > 0) {
        setExpanded(true);
      } else {
        setExpanded(false);
      }
    }

    setIsDragging(false);
    setStartY(0);
    setCurrentY(0);
  };

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const handleWheel = (e) => {
      if (containerRef.current && containerRef.current.contains(e.target)) {
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          e.preventDefault();
        }
      }
    };

    if (expanded && containerRef.current) {
      containerRef.current.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        if (containerRef.current) {
          containerRef.current.removeEventListener('wheel', handleWheel);
        }
      };
    }
  }, [expanded]);

  useEffect(() => {
    if (selectedActionCard && isMountedRef.current) {
      setExpanded(false);
    }
  }, [selectedActionCard]);

  const canRedraw = myTurn && (actionsRemaining[name] || 0) > 0 && !hasUsedRedrawThisTurn && playerActionCards.length > 0;

  return (
    <div 
      ref={containerRef}
      className={`mobile-hand-container ${expanded ? 'mobile-hand-expanded' : 'mobile-hand-collapsed'}`}
      data-tutorial="your-hand"
    >
      {/* Swipe Handle */}
      <div 
        className="mobile-hand-handle"
        onClick={handleToggle}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="mobile-hand-handle-bar" />
      </div>

      {/* Collapsed View */}
      {!expanded && (
        <div className="mobile-hand-collapsed-content">
          <div className="mobile-hand-card-count">
            <span className="text-xl">{redrawMode ? '🔄' : '🃏'}</span>
            <span>
              {redrawMode ? `Select to Discard (${selectedForDiscard.size}/3)` : `Your Hand (${playerActionCards.length}/${maxActionCards})`}
            </span>
          </div>
          {!redrawMode && canDrawActionCard && !handFull && (
            <button
              className="mobile-hand-draw-button"
              onClick={onDrawActionCard}
            >
              + Draw Card
            </button>
          )}
          {redrawMode && (
            <div className="hand-redraw-active-controls">
              <button className="hand-redraw-confirm" onClick={onConfirmRedraw} disabled={selectedForDiscard.size === 0}>
                ✓ Confirm ({selectedForDiscard.size})
              </button>
              <button className="hand-redraw-cancel" onClick={onCancelRedraw}>
                ✕ Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {/* Expanded View */}
      {expanded && (
        <div className="mobile-hand-expanded-content">
          <div className="mobile-hand-expanded-header">
            <div className="mobile-hand-card-count">
              <span className="text-xl">{redrawMode ? '🔄' : '🃏'}</span>
              <span>
                {redrawMode ? `Select to Discard (${selectedForDiscard.size}/3)` : `Your Hand (${playerActionCards.length}/${maxActionCards})`}
              </span>
            </div>
            {!redrawMode && (
              <div className="hand-redraw-controls">
                {canRedraw && (
                  <button className="hand-redraw-button" onClick={onEnterRedrawMode}>
                    🔄 Discard & Redraw
                  </button>
                )}
                {canDrawActionCard && !handFull && (
                  <button className="mobile-hand-draw-button" onClick={onDrawActionCard}>
                    + Draw Card
                  </button>
                )}
              </div>
            )}
            {redrawMode && (
              <div className="hand-redraw-active-controls">
                <button className="hand-redraw-confirm" onClick={onConfirmRedraw} disabled={selectedForDiscard.size === 0}>
                  ✓ Confirm ({selectedForDiscard.size})
                </button>
                <button className="hand-redraw-cancel" onClick={onCancelRedraw}>
                  ✕ Cancel
                </button>
              </div>
            )}
          </div>

          {/* Cards */}
          <div className="mobile-hand-cards-scroll mobile-hand-desktop-sizes">
            {playerActionCards.length === 0 ? (
              <div className="text-center p-xl px-lg text-tertiary text-base w-full">
                <div className="text-2xl mb-sm">🃏</div>
                <div>No action cards</div>
                <div className="text-sm mt-xs">
                  Draw a card to get started
                </div>
              </div>
            ) : (
              playerActionCards.map((action, index) => {
                const isNew = newActionCardIds instanceof Set 
                  ? newActionCardIds.has(action.id)
                  : Array.isArray(newActionCardIds) && newActionCardIds.includes(action.id);
                const isSelected = redrawMode 
                  ? selectedForDiscard.has(action.id)
                  : (selectedActionCard && selectedActionCard.id === action.id);

                return (
                  <div 
                    key={action.id || index}
                    className="mobile-hand-action-card"
                    style={{
                      animation: isNew ? 'slideInFromRight 0.5s ease-out' : 'none',
                    }}
                  >
                    <ActionCardView
                      action={action}
                      onSelect={redrawMode ? onToggleDiscardSelection : (selectedAction) => {
                        handleSelectActionCard(selectedAction);
                        setExpanded(false);
                      }}
                      isSelected={isSelected}
                      canPlay={redrawMode ? true : (myTurn && (actionsRemaining[name] || 0) > 0)}
                      styleCardSize={styleCardSize}
                      isDiscardMode={redrawMode}
                    />
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileHand;
